import React, { useState, useEffect, useRef } from "react";
import { X, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import gsap from "gsap";
import { WEBSITE_DETAILS } from "@/config";
import { openExternalLink } from "@/utils/contactActions";

const ExitIntentModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasTriggered, setHasTriggered] = useState(false);
    const modalRef = useRef(null);
    const overlayRef = useRef(null);

    useEffect(() => {
        const handleMouseLeave = (e) => {
            // Trigger when mouse leaves the top of the viewport
            if (e.clientY <= 0 && !hasTriggered) {
                // Recommended: Show once every 24 hours to avoid annoyance
                const lastShown = localStorage.getItem("exitIntentLastShown");
                const now = Date.now();
                const oneDay = 24 * 60 * 60 * 1000;

                if (!lastShown || now - parseInt(lastShown) > oneDay) {
                    setIsOpen(true);
                    setHasTriggered(true);
                    localStorage.setItem("exitIntentLastShown", now.toString());
                }
            }
        };

        document.addEventListener("mouseleave", handleMouseLeave);

        return () => {
            document.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, [hasTriggered]);

    useEffect(() => {
        if (isOpen) {
            const ctx = gsap.context(() => {
                gsap.fromTo(
                    overlayRef.current,
                    { opacity: 0 },
                    { opacity: 1, duration: 0.3 }
                );
                gsap.fromTo(
                    modalRef.current,
                    { scale: 0.8, opacity: 0, y: 20 },
                    { scale: 1, opacity: 1, y: 0, duration: 0.4, ease: "back.out(1.7)" }
                );
            });
            return () => ctx.revert();
        }
    }, [isOpen]);

    const handleClose = () => {
        gsap.context(() => {
            gsap.to(overlayRef.current, { opacity: 0, duration: 0.2 });
            gsap.to(modalRef.current, {
                scale: 0.8,
                opacity: 0,
                y: 20,
                duration: 0.2,
                onComplete: () => setIsOpen(false),
            });
        });
    };

    const handleClaim = () => {
        openExternalLink(WEBSITE_DETAILS.socialMedia.whatsapp);
        handleClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Overlay */}
            <div
                ref={overlayRef}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleClose}
            ></div>

            {/* Modal */}
            <div
                ref={modalRef}
                className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
                {/* Header with Pattern */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-center text-white">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-md">
                        <Gift className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold">Wait! Don't Miss Out</h2>
                    <p className="text-blue-100">We have a special gift for you.</p>
                    <button
                        onClick={handleClose}
                        className="absolute right-4 top-4 rounded-full bg-white/10 p-1 text-white hover:bg-white/20 transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 text-center">
                    <p className="mb-6 text-gray-600">
                        Get a <strong>FREE Consultation</strong> and a customized roadmap for
                        your website project. No strings attached!
                    </p>

                    <div className="flex flex-col gap-3">
                        <Button
                            size="lg"
                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                            onClick={handleClaim}
                        >
                            Claim My Free Consultation
                        </Button>
                        <Button variant="ghost" className="w-full" onClick={handleClose}>
                            No thanks, I'll pass
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExitIntentModal;
